import { useEffect, useState } from 'react';
import { Box, CircularProgress, Divider, Grid, IconButton, Modal, Typography, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ZTaskService from '../../services/ztask.service';

const Row = ({ label, value, isDarkMode }) => {
    if (!value && value !== 0) return null;
    return (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 600, color: isDarkMode ? '#90caf9' : '#1565c0', minWidth: 100, flexShrink: 0 }}>
                {label}:
            </Typography>
            <Typography sx={{ fontSize: 11, color: isDarkMode ? '#cfd8dc' : '#37474f', wordBreak: 'break-word' }}>
                {String(value)}
            </Typography>
        </Box>
    );
};

function TareaCard({ tarea, isDarkMode, isQueried }) {
    const getStatusConfig = (estado) => {
        const configs = {
            'Cerrada':           { bar: '#4caf50', bg: isDarkMode ? '#1b3a2d' : '#e8f5e9', border: isDarkMode ? '#2e7d32' : '#a5d6a7', text: isDarkMode ? '#81c784' : '#2e7d32' },
            'Asignada':          { bar: '#2196f3', bg: isDarkMode ? '#1a2d3d' : '#e3f2fd', border: isDarkMode ? '#1565c0' : '#90caf9', text: isDarkMode ? '#64b5f6' : '#1565c0' },
            'En Desplazamiento': { bar: '#ff9800', bg: isDarkMode ? '#3a2a10' : '#fff8e1', border: isDarkMode ? '#e65100' : '#ffb300', text: isDarkMode ? '#ffb74d' : '#e65100' },
            'En Atendimiento':   { bar: '#9c27b0', bg: isDarkMode ? '#2d1a3a' : '#f3e5f5', border: isDarkMode ? '#6a1b9a' : '#ce93d8', text: isDarkMode ? '#ce93d8' : '#6a1b9a' },
        };
        return configs[estado] || { bar: '#ff9800', bg: isDarkMode ? '#3a2020' : '#fff3e0', border: isDarkMode ? '#bf360c' : '#ffcc80', text: isDarkMode ? '#ffb74d' : '#e65100' };
    };

    const sc = getStatusConfig(tarea.estado_ot);
    const tel1 = tarea.datosCliente_ot?.clienteTelefono_ot;
    const tel2 = tarea.datosCliente_ot?.clienteTelefono2_ot;
    const telefonos = [tel1, tel2].filter(t => t && t.trim()).join(' / ') || null;
    const bajada = tarea.datosPlan?.anchoBajada ? `${parseFloat(tarea.datosPlan.anchoBajada).toFixed(0)} Mb↓` : null;
    const planLabel = tarea.datosPlan?.nombrePlan ? `${tarea.datosPlan.nombrePlan}${bajada ? ` · ${bajada}` : ''}` : null;
    const horaFin = tarea.fechaProgramadaFin ? tarea.fechaProgramadaFin.split(' ')[1] : null;
    const ultimoAtend = tarea.atendimiento_ot?.length ? tarea.atendimiento_ot[tarea.atendimiento_ot.length - 1] : null;

    return (
        <Box sx={{
            borderRadius: '10px',
            border: isDarkMode ? '1px solid #3d4a62' : '1px solid #dce6f5',
            borderLeft: `4px solid ${sc.bar}`,
            overflow: 'hidden',
            ...(isQueried && {
                '@keyframes cardGlow': {
                    '0%':   { boxShadow: `0 0 0 0 ${sc.bar}55` },
                    '50%':  { boxShadow: `0 0 12px 5px ${sc.bar}35` },
                    '100%': { boxShadow: `0 0 0 0 ${sc.bar}55` },
                },
                animation: 'cardGlow 2s ease-in-out infinite',
            }),
        }}>
            {/* Header */}
            <Box sx={{
                display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1,
                px: 1.5, py: 1,
                background: isDarkMode ? '#162030' : '#e8f0fe',
            }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 13, color: isDarkMode ? '#90caf9' : '#1565c0', lineHeight: 1.3 }}>
                        {tarea.datosCliente_ot?.clienteNombre_ot || `Tarea #${tarea.tareaId_ot}`}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.8, mt: 0.3, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Typography sx={{ fontSize: 10.5, color: isDarkMode ? '#546e7a' : '#90a4ae', fontWeight: 500 }}>
                            OT #{tarea.nroOTText_ot || tarea.tareaId_ot}
                        </Typography>
                        {tarea.ticketId_ot && (
                            <Typography sx={{ fontSize: 10.5, color: isDarkMode ? '#546e7a' : '#90a4ae', fontWeight: 500 }}>
                                · Ticket #{tarea.ticketId_ot}
                            </Typography>
                        )}
                        {isQueried && (
                            <Box sx={{
                                display: 'inline-flex', alignItems: 'center', gap: 0.4,
                                px: 0.8, py: 0.2, borderRadius: '8px',
                                background: isDarkMode ? '#1a2d1a' : '#fff9c4',
                                border: isDarkMode ? '1px solid #388e3c' : '1px solid #f9a825',
                                '@keyframes badgePulse': {
                                    '0%':   { transform: 'scale(1)',   opacity: 1 },
                                    '50%':  { transform: 'scale(1.08)', opacity: 0.8 },
                                    '100%': { transform: 'scale(1)',   opacity: 1 },
                                },
                                animation: 'badgePulse 1.4s ease-in-out infinite',
                            }}>
                                <Box sx={{
                                    width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                                    background: isDarkMode ? '#66bb6a' : '#f9a825',
                                    '@keyframes dotBlink': {
                                        '0%, 100%': { opacity: 1 },
                                        '50%':      { opacity: 0.3 },
                                    },
                                    animation: 'dotBlink 1.4s ease-in-out infinite',
                                }} />
                                <Typography sx={{ fontSize: 9, fontWeight: 700, color: isDarkMode ? '#a5d6a7' : '#856404', whiteSpace: 'nowrap' }}>
                                    OT consultada
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
                <Box sx={{ px: 1, py: 0.3, borderRadius: '10px', flexShrink: 0, background: sc.bg, border: `1px solid ${sc.border}` }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: sc.text, whiteSpace: 'nowrap' }}>
                        {tarea.estado_ot}
                    </Typography>
                </Box>
            </Box>

            {/* Body */}
            <Box sx={{ px: 1.5, py: 1, background: isDarkMode ? '#1a2535' : '#f8faff', display: 'flex', flexDirection: 'column', gap: 0.5 }}>

                {/* Schedule band */}
                {(tarea.strFechaProgramada || tarea.strHoraProgramada) && (
                    <Box sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        px: 1, py: 0.5, mb: 0.3, borderRadius: '6px',
                        background: isDarkMode ? '#0f1a28' : '#e0ebff',
                    }}>
                        <Typography sx={{ fontSize: 10.5, fontWeight: 600, color: isDarkMode ? '#90caf9' : '#1565c0' }}>
                            {tarea.strFechaProgramada}
                        </Typography>
                        <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: isDarkMode ? '#90caf9' : '#1565c0' }}>
                            {tarea.strHoraProgramada}{horaFin && ` – ${horaFin}`}
                            {tarea.tiempoEstimado && ` (${tarea.tiempoEstimado} min)`}
                        </Typography>
                    </Box>
                )}

                {/* Tags: segment + class */}
                {(tarea.segmentoOT || tarea.claseOT) && (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.2 }}>
                        {tarea.segmentoOT && (
                            <Box sx={{ px: 0.8, py: 0.2, borderRadius: '4px', background: isDarkMode ? '#1e3045' : '#dbeafe', border: isDarkMode ? '1px solid #2d4a6a' : '1px solid #bfdbfe' }}>
                                <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: isDarkMode ? '#60a5fa' : '#1d4ed8' }}>
                                    {tarea.segmentoOT}
                                </Typography>
                            </Box>
                        )}
                        {tarea.claseOT && (
                            <Box sx={{ px: 0.8, py: 0.2, borderRadius: '4px', background: isDarkMode ? '#1e3d2d' : '#dcfce7', border: isDarkMode ? '1px solid #2d6a3a' : '1px solid #bbf7d0' }}>
                                <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: isDarkMode ? '#4ade80' : '#15803d' }}>
                                    {tarea.claseOT}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}

                <Row label="Tipo"        value={tarea.tipoOT}       isDarkMode={isDarkMode} />
                <Row label="Dirección"   value={tarea.direccion_ot} isDarkMode={isDarkMode} />
                <Row label="Teléfono"    value={telefonos}          isDarkMode={isDarkMode} />
                <Row label="Plan"        value={planLabel}          isDarkMode={isDarkMode} />
                {tarea.observacionesCierre_ot && tarea.observacionesCierre_ot !== '-' && (
                    <Row label="Obs. Cierre" value={tarea.observacionesCierre_ot} isDarkMode={isDarkMode} />
                )}

                {/* Activity summary */}
                {(ultimoAtend || tarea.desplazamiento_ot?.length > 0) && (
                    <Box sx={{
                        display: 'flex', gap: 1.5, mt: 0.5, flexWrap: 'wrap',
                        pt: 0.6, borderTop: isDarkMode ? '1px solid #2d3a4d' : '1px solid #dce6f5',
                    }}>
                        {ultimoAtend && (
                            <Typography sx={{ fontSize: 10, color: isDarkMode ? '#78909c' : '#90a4ae' }}>
                                Atendimiento: {ultimoAtend.minutosTranscurridos} min
                            </Typography>
                        )}
                        {tarea.desplazamiento_ot?.length > 0 && (
                            <Typography sx={{ fontSize: 10, color: isDarkMode ? '#78909c' : '#90a4ae' }}>
                                {tarea.desplazamiento_ot.length} desplazamiento{tarea.desplazamiento_ot.length !== 1 ? 's' : ''}
                            </Typography>
                        )}
                    </Box>
                )}
            </Box>
        </Box>
    );
}

function ModalTareasCuadrilla({ show, hideModal, nroOT }) {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    const [tareas, setTareas] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (!nroOT) return;
        setIsLoading(true);
        ZTaskService.getTareasCuadrillaByOT(nroOT)
            .then((resp) => setTareas(resp?.tareas || []))
            .catch(() => setTareas([]))
            .finally(() => setIsLoading(false));
    }, [nroOT]);

    return (
        <Modal open={show} onClose={hideModal}>
            <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: { xs: '95%', md: '50%' },
                maxHeight: '80vh',
                overflowY: 'auto',
                background: isDarkMode ? '#1F2A40' : '#fff',
                border: isDarkMode ? '1px solid #3d4a62' : '1px solid #ccc',
                borderRadius: 2,
                boxShadow: 24,
                p: 2,
            }}>
                <Grid container>
                    <Grid item xs={11} sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant='h5' sx={{ color: isDarkMode ? '#90caf9' : '#1565c0', fontWeight: 700, fontSize: 14 }}>
                            {`Tareas de la Cuadrilla - OT #${nroOT}`}
                        </Typography>
                    </Grid>
                    <Grid item xs={1} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <IconButton size='small' onClick={hideModal}>
                            <CloseIcon sx={{ color: isDarkMode ? '#e0e0e0' : '#000', fontSize: 18 }} />
                        </IconButton>
                    </Grid>
                </Grid>
                <Divider sx={{ backgroundColor: isDarkMode ? '#3d4a62' : '#ccc', my: 1 }} />

                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress size={28} />
                    </Box>
                ) : tareas.length === 0 ? (
                    <Typography sx={{ textAlign: 'center', py: 3, color: isDarkMode ? '#b0bec5' : 'gray', fontSize: 12 }}>
                        No se encontraron tareas para esta cuadrilla
                    </Typography>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {tareas.map((tarea, idx) => (
                            <TareaCard key={tarea.id_ot || idx} tarea={tarea} isDarkMode={isDarkMode} isQueried={String(tarea.nroOTText_ot) === String(nroOT)} />
                        ))}
                    </Box>
                )}
            </Box>
        </Modal>
    );
}


export default ModalTareasCuadrilla;

